/**
 * gridHeaderChrome — the header-cell affordance geometry BOTH report grids share.
 *
 * spec 121. A plain leaf module rather than a constant exported from either grid,
 * for the same reason `PIN_CELL_CLASS` lives in `src/utils/columnPinning.js` and
 * `columnDefinitionGate.js` is its own file: `src/components/reports/ReportGroupedGrid.jsx`
 * does not import `DataGrid.jsx` and must not start — importing a 2000-line grid plus
 * TanStack to reach a class string is a real cost, and the reverse direction is worse
 * (`DataGrid.jsx` → `ColumnValuePicker` → `ControlCheckbox` is already a live chain).
 * This module imports nothing.
 *
 * ## Why it exists at all
 *
 * spec 116 gave the flat grid's reorder handle a 24x24 target (B2) and its resize
 * divider an inset 16px hairline (B1), and could not touch `ReportGroupedGrid.jsx`
 * because that round was partitioned by file. The result shipped for six days: the
 * 33 flat reports had a 24x24 handle and the **86 grouped ones** — the majority
 * bucket — kept a ~7-10px one. A hand-copied constant is exactly how the two grids
 * diverged the first time (the strip and the grip were once described in both files
 * as "byte-identical … change one, change the other", and then one changed), so the
 * box is defined ONCE here and imported by both.
 *
 * ## The box, and why each margin is the number it is
 *
 * 24x24 is SC 2.5.8's floor. `flex-none` is NOT optional: `w-6` alone leaves
 * `flex: 0 1 auto` + `min-width: auto`, so as a flex item of the header's
 * `flex items-center gap-1` row the automatic minimum resolves to
 * `min(24px, the glyph's ~10px min-content)` and the box SHRINKS on any column whose
 * header content is wider than the column — measured in Chrome for spec 116 at
 * 24.00 / 10.08 / 7.11 px across three column widths. It tests green in jsdom either
 * way, which is how it shipped once already.
 *
 * `-my-1` — the box is 24px tall but contributes 24 - 8 = 16px to the flex line, so
 * no header row gets taller; the 4px it overhangs top and bottom lands inside the
 * `th`'s own vertical padding (`py-3` = 12px on the flat grid, `py-2` = 8px on the
 * grouped one — 4px < 8px, so it is absorbed there too, though jsdom applies no CSS
 * and cannot prove it: see spec 121 §6).
 *
 * `-mr-1` — reclaims the 4px the box would otherwise push the sort button across by,
 * so the sort button starts flush beside the target rather than on top of it.
 * Overlap would be won by the sort button (a real control, later in DOM order).
 *
 * ## ⚠️ The left margin is per-grid, and copying it is a 3px bug
 *
 * `-ml-[3px]` is arithmetic tuned to a `px-4` header cell and is WRONG on a `px-3`
 * one. Both grids' resize strips are `w-[25px] -right-[12px]`, i.e. a `z-10`
 * `cursor-col-resize` element overhanging **12px** into the NEXT header cell.
 * Measured from that cell's content box (x = 0 at the padding edge):
 *
 *   - flat `DataGrid` th, `px-4` (16px): the strip owns x = -16..-4, so a -3px left
 *     margin sits 1px clear of it. This is `HANDLE_BOX_PX4`.
 *   - grouped `ReportGroupedGrid` th, `px-3` (12px): the strip owns x = -12..**0**,
 *     i.e. right up to the content-box edge, so the SAME -3px margin buries the
 *     leftmost 3 of the 24 px under a live resize strip. That is a target that
 *     measures 24px and behaves like 21 — the round-1 mistake `DataGrid.jsx`'s own
 *     strip comment records ("0px on the right"). So the grouped grid gets
 *     `HANDLE_BOX_PX3`, with **no** negative left margin.
 *
 * Neither constant is interchangeable with the other. If a third grid ever adopts
 * this, do the subtraction for its own padding rather than picking whichever one
 * looks closer.
 */
const BOX = 'inline-flex h-6 w-6 flex-none items-center justify-center -my-1 -mr-1'

/** The shared 24x24 box for a `px-4` header cell (the flat `DataGrid`). */
export const HANDLE_BOX_PX4 = `${BOX} -ml-[3px]`

/** The same box for a `px-3` header cell (`ReportGroupedGrid`) — no left margin. */
export const HANDLE_BOX_PX3 = BOX

/**
 * The resize divider's `::after` — the RESTING half, shared by interpolation so the
 * two grids cannot drift on it again.
 *
 * `DIVIDER_BASE` is the positioning that never changes; `DIVIDER_RESTING` is the
 * 16px centred 1px `--color-rc-control` hairline (spec 116 B1: "not edge to edge",
 * Himaad 2026-09-03 — the geometry half of his ask was taken and the "a shade or two
 * lighter" half was NOT, because no colour lighter than #8b95a5 clears SC 1.4.11 on
 * a #f9fafb header. See spec 116 §1 and §6 for the disclosed 2.896:1 near-miss).
 *
 * ⚠️ The REVEALED half is deliberately NOT here, and this is a Tailwind constraint,
 * not an oversight. Tailwind v4 extracts candidates from source TEXT, so a variant
 * assembled at run time (`` `${prefix}after:h-6` ``) emits no CSS at all — the
 * utility silently does not exist. The flat grid reveals via a `getIsResizing()`
 * ternary and the grouped grid via `hover:` / `focus:` / `active:` (it hand-rolls
 * resize and has no isResizing state), so those strings must be written out
 * literally at each call site. What holds them together is therefore a PROPERTY
 * assertion in `headerAffordances.grids.test.jsx` — every state that thickens the
 * mark must also lengthen it — rather than a shared string.
 *
 * That property is the one that matters: the hover-revealed grip lozenge is
 * `h-4 w-1`, so a 4px bead over a 16px line covers it end to end and re-creates the
 * "permanent nub standing in for a boundary that is not drawn" that spec 098's B4
 * removed. 24px against the grip's 16px leaves 4px of rule above and below. Before
 * spec 121 the grouped grid's mark thickened (1px → 2px) and recoloured but carried
 * no `after:h-*` token in ANY state, so a naive `after:inset-y-0` → `after:h-4` swap
 * would have produced exactly that nub on 86 reports.
 */
export const DIVIDER_BASE = "after:absolute after:top-1/2 after:-translate-y-1/2 after:transition-all after:content-['']"

export const DIVIDER_RESTING = 'after:h-4 after:w-px after:bg-rc-control'
